import { useState, useEffect } from 'react';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Database,
  TrendingUp,
  AlertTriangle,
  ExternalLink,
  Calendar,
  Filter,
  Download,
  Loader2,
  Info,
  History,
  X,
} from 'lucide-react';
import api from '../../services/api';

export default function CrawlerStatus() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [signals, setSignals] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 100, offset: 0 });
  const [filters, setFilters] = useState({
    source_name: '',
    buyer_id: '',
  });
  const [selectedError, setSelectedError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historySource, setHistorySource] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    loadStatus();
    loadData();
  }, [pagination.offset, filters]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/crawler/status');
      if (response.data?.success) {
        setStatus(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load crawler status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (sourceName) => {
    try {
      setHistoryLoading(true);
      setHistorySource(sourceName);
      setShowHistory(true);
      const response = await api.get(`/admin/crawler/history?source_name=${encodeURIComponent(sourceName)}&limit=20`);
      if (response.data?.success) {
        setHistoryData(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load crawler history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setDataLoading(true);
      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
        ...filters,
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await api.get('/admin/crawler/data', { params });
      if (response.data?.success) {
        setSignals(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      }
    } catch (error) {
      console.error('Failed to load crawler data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (lastCrawled) => {
    if (!lastCrawled) return 'text-gray-500';
    const date = new Date(lastCrawled);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 24) return 'text-green-600';
    if (diffHours < 72) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (lastCrawled) => {
    if (!lastCrawled) return XCircle;
    const date = new Date(lastCrawled);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 24) return CheckCircle;
    if (diffHours < 72) return AlertTriangle;
    return XCircle;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crawler Status & Data</h1>
          <p className="text-gray-600">Monitor crawler activity and view collected signals</p>
        </div>
        <button
          onClick={() => { loadStatus(); loadData(); }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Recent Stats */}
      {status?.recentStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Signals (Last 7 Days)"
            value={status.recentStats.signalsLast7Days}
            icon={Database}
            color="blue"
          />
          <StatCard
            title="Buyers with Signals"
            value={status.recentStats.buyersWithSignals}
            icon={TrendingUp}
            color="green"
          />
          <StatCard
            title="Latest Signal"
            value={formatTimeAgo(status.recentStats.latestSignal)}
            icon={Clock}
            color="purple"
          />
        </div>
      )}

      {/* Data Sources Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Sources Status</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Source</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Last Crawled</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Signals Collected</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Errors</th>
                <th className="text-center py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {status?.sources?.map((source) => {
                const StatusIcon = getStatusIcon(source.last_crawled);
                const statusColor = getStatusColor(source.last_crawled);
                return (
                  <tr key={source.source_type} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{source.source_name}</div>
                      <div className="text-xs text-gray-500">{source.source_type}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`w-4 h-4 ${statusColor}`} />
                        <span className={statusColor}>
                          {source.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className={statusColor}>
                        {formatTimeAgo(source.last_crawled)}
                      </div>
                      {source.last_crawled && (
                        <div className="text-xs text-gray-500">
                          {formatDateTime(source.last_crawled)}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-gray-900">
                      {source.signals_collected || 0}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {source.error_count > 0 ? (
                        <button
                          onClick={() => setSelectedError({
                            source: source.source_name,
                            count: source.error_count,
                            message: source.last_error,
                            timestamp: source.updated_at
                          })}
                          className="flex items-center justify-end gap-1 text-red-600 hover:text-red-700 transition-colors cursor-pointer"
                          title="Click to view error details"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium">{source.error_count}</span>
                          <Info className="w-3 h-3 opacity-50" />
                        </button>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => loadHistory(source.source_name)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        title="View crawler run history"
                      >
                        <History className="w-4 h-4" />
                        <span>History</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Signals by Date Chart */}
      {status?.signalsByDate && status.signalsByDate.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Signals Collected (Last 30 Days)</h2>
          <div className="space-y-2">
            {status.signalsByDate.slice(0, 14).map((day) => {
              const maxCount = Math.max(...status.signalsByDate.map(d => d.count));
              const percentage = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
              return (
                <div key={day.date} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-gray-600">{day.date}</div>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="w-16 text-right text-sm font-medium text-gray-900">
                    {day.count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Collected Data */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Collected Signals</h2>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filters.source_name}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, source_name: e.target.value }));
                setPagination(prev => ({ ...prev, offset: 0 }));
              }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">All Sources</option>
              {status?.signalsBySource?.map((s) => (
                <option key={s.source_name} value={s.source_name}>
                  {s.source_name} ({s.count})
                </option>
              ))}
            </select>
          </div>
        </div>

        {dataLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-red-600" />
          </div>
        ) : signals.length === 0 ? (
          <div className="text-center py-12">
            <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No signals found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Buyer</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Type</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Source</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Title</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Strength</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {signals.map((signal) => (
                    <tr key={signal.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="text-gray-900">
                          {new Date(signal.signal_date || signal.created_at).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTimeAgo(signal.created_at)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">
                          {signal.buyer_name || 'Unknown'}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                          {signal.signal_type?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900">{signal.source_name || 'Unknown'}</div>
                        {signal.source_url && (
                          <a
                            href={signal.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                          >
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900 line-clamp-2 max-w-md">
                          {signal.signal_title || signal.signal_description || 'No title'}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {signal.signal_strength ? (
                          <span className="font-medium text-gray-900">
                            {signal.signal_strength}/10
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} signals
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                    disabled={pagination.offset === 0}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Error Details Modal */}
      {selectedError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-red-50 border-b border-red-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Crawler Error Details</h3>
                    <p className="text-sm text-gray-600">{selectedError.source}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedError(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              {/* Error Count */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Total Errors</span>
                  <span className="text-2xl font-bold text-red-600">{selectedError.count}</span>
                </div>
                {selectedError.timestamp && (
                  <div className="mt-2 text-xs text-gray-500">
                    Last updated: {formatDateTime(selectedError.timestamp)}
                  </div>
                )}
              </div>

              {/* Error Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Error Message
                </label>
                {selectedError.message ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono">
                      {selectedError.message}
                    </pre>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-500 italic">No error message available</p>
                  </div>
                )}
              </div>

              {/* Common Issues Help */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Common Issues & Solutions
                </h4>
                <ul className="text-sm text-blue-800 space-y-1 ml-6 list-disc">
                  <li>Check if the source website is accessible</li>
                  <li>Verify API credentials and rate limits</li>
                  <li>Review crawler logs for detailed stack traces</li>
                  <li>Ensure Playwright browsers are installed (for web scrapers)</li>
                  <li>Check database connection and permissions</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setSelectedError(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  loadStatus(); // Refresh status
                  setSelectedError(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Crawler History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-indigo-50 border-b border-indigo-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <History className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Crawler Run History</h3>
                    <p className="text-sm text-gray-600">{historySource}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No crawler run history found for this source.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Date & Time</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Duration</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Signals</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Deals</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Catalysts</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Total Items</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {historyData.map((run) => {
                        const hasNewItems = (run.signals_found > 0 || run.deals_found > 0 || run.catalysts_found > 0);
                        const statusColor = run.status === 'completed' ? 'text-green-600' :
                                          run.status === 'failed' ? 'text-red-600' :
                                          run.status === 'partial' ? 'text-yellow-600' : 'text-gray-600';
                        const StatusIcon = run.status === 'completed' ? CheckCircle :
                                          run.status === 'failed' ? XCircle :
                                          Clock;

                        return (
                          <tr key={run.id} className="hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div className="text-gray-900">
                                {new Date(run.started_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(run.started_at).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center text-gray-700">
                              {run.duration_seconds ? `${run.duration_seconds}s` : '-'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`font-medium ${run.signals_found > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                {run.signals_found || 0}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`font-medium ${run.deals_found > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                                {run.deals_found || 0}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`font-medium ${run.catalysts_found > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                                {run.catalysts_found || 0}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center text-gray-600">
                              {run.total_items_processed || 0}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-col items-center gap-1">
                                <div className={`flex items-center gap-1.5 ${statusColor}`}>
                                  <StatusIcon className="w-4 h-4" />
                                  <span className="text-xs font-medium capitalize">{run.status}</span>
                                </div>
                                {!hasNewItems && run.status === 'completed' && (
                                  <span className="text-xs text-gray-500 italic">No new items</span>
                                )}
                                {run.error_count > 0 && (
                                  <span className="text-xs text-red-600">
                                    {run.error_count} {run.error_count === 1 ? 'error' : 'errors'}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Legend */}
              {historyData.length > 0 && (
                <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Legend</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-600 rounded"></div>
                      <span className="text-gray-600">Signals - BD intelligence</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-600 rounded"></div>
                      <span className="text-gray-600">Deals - Partnership announcements</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-600 rounded"></div>
                      <span className="text-gray-600">Catalysts - Future events</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowHistory(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-600">{title}</p>
    </div>
  );
}




